import { useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { PLATFORM_FEE_CENTS } from '@dermalink/shared';
import { PageLayout, TariffList } from '@/components';
import { ChevronDownIcon } from '../../components/icons';
import logoIcon from '../../assets/logo-icon-color.svg';
import styles from './PlatformInfo.module.css';

function formatEuroFromCents(cents: number): string {
  return new Intl.NumberFormat('nl-BE', { style: 'currency', currency: 'EUR' }).format(cents / 100);
}

const FAQ_ITEMS: { question: string; answer: React.ReactNode; id?: string }[] = [
  {
    question: 'Hoe werkt een teleconsultatie bij Dermalink?',
    answer: (
      <>
        <p className={styles.p}>
          Een teleconsultatie verloopt volledig online, in enkele stappen. De communicatie is asynchroon: u en de dermatoloog zijn niet op hetzelfde moment online.
        </p>
        <ol className={styles.stepList}>
          <li className={styles.stepItem}>
            <span className={styles.stepNumber}>1</span>
            <div>
              <p className={styles.stepTitle}>Aanmelden</p>
              <p className={styles.stepDesc}>U meldt zich aan met uw e-mailadres en rijksregisternummer.</p>
            </div>
          </li>
          <li className={styles.stepItem}>
            <span className={styles.stepNumber}>2</span>
            <div>
              <p className={styles.stepTitle}>Intakevragen</p>
              <p className={styles.stepDesc}>U beantwoordt enkele korte veiligheidsvragen, bijvoorbeeld over medicatie, allergieën of een zwangerschap.</p>
            </div>
          </li>
          <li className={styles.stepItem}>
            <span className={styles.stepNumber}>3</span>
            <div>
              <p className={styles.stepTitle}>Uw huidprobleem</p>
              <p className={styles.stepDesc}>U voegt een of meerdere duidelijke foto&apos;s toe en beschrijft uw klacht.</p>
            </div>
          </li>
          <li className={styles.stepItem}>
            <span className={styles.stepNumber}>4</span>
            <div>
              <p className={styles.stepTitle}>Beoordeling</p>
              <p className={styles.stepDesc}>Uw dermatoloog bekijkt uw aanvraag en antwoordt doorgaans binnen 5 werkdagen.</p>
            </div>
          </li>
          <li className={styles.stepItem}>
            <span className={styles.stepNumber}>5</span>
            <div>
              <p className={styles.stepTitle}>Diagnose en advies</p>
              <p className={styles.stepDesc}>U ontvangt een waarschijnlijke diagnose, een behandeladvies en indien nodig een elektronisch voorschrift.</p>
            </div>
          </li>
          <li className={styles.stepItem}>
            <span className={styles.stepNumber}>6</span>
            <div>
              <p className={styles.stepTitle}>Betaling</p>
              <p className={styles.stepDesc}>U ontvangt een e-mail zodra de dermatoloog om betaling vraagt, via onze betaaldienst Mollie. Tot u betaald heeft, blijft de status &apos;Betaling vereist&apos; zichtbaar op uw overzicht.</p>
            </div>
          </li>
          <li className={styles.stepItem}>
            <span className={styles.stepNumber}>7</span>
            <div>
              <p className={styles.stepTitle}>Overzicht</p>
              <p className={styles.stepDesc}>U ontvangt een pdf met een overzicht van uw teleconsultatie, zodat u alles later rustig kan nalezen.</p>
            </div>
          </li>
        </ol>
      </>
    ),
  },
  {
    question: 'Hoe is de prijs opgebouwd?',
    id: 'prijs-opgebouwd',
    answer: (
      <>
        <p className={styles.p}>
          De dermatoloog bepaalt per gesprek welk type consult van toepassing is:
        </p>
        <div className={styles.tariffListWrap}>
          <TariffList />
        </div>
        <br />
        <p className={styles.p}>
          Van de prijs van een teleconsultatie gaan eerst kosten af; wat overblijft is de vergoeding voor de arts.
        </p>
        <dl className={styles.dl}>
          <div className={styles.dlRow}>
            <dt className={styles.dt}>Btw</dt>
            <dd className={styles.dd}>
              Op uw consultatie wordt geen btw aangerekend: medische zorg is vrijgesteld van btw.
            </dd>
          </div>
          <div className={styles.dlRow}>
            <dt className={styles.dt}>Platformkosten</dt>
            <dd className={styles.dd}>
              Per betaald consult betaalt de praktijk een vaste vergoeding aan Dermalink ({formatEuroFromCents(PLATFORM_FEE_CENTS.new_consult)} bij een nieuw consult, {formatEuroFromCents(PLATFORM_FEE_CENTS.prescription)} bij een voorschrift, inclusief 21% btw) voor ontwikkeling, onderhoud, veilige opslag van uw gegevens en beheer van het platform.
            </dd>
          </div>
          <div className={styles.dlRow}>
            <dt className={styles.dt}>Betalingsprovider</dt>
            <dd className={styles.dd}>Online betalingen en terugstortingen lopen via een erkende betaaldienst, die daarvoor een kost aanrekent aan de praktijk.</dd>
          </div>
          <div className={styles.dlRow}>
            <dt className={styles.dt}>Honorarium arts</dt>
            <dd className={styles.dd}>De praktijk van de arts ontvangt het resterende bedrag als vergoeding voor de consultatie.</dd>
          </div>
        </dl>
      </>
    ),
  },
  {
    question: 'Hoe snel krijg ik antwoord?',
    answer: (
      <>
        Wij streven ernaar u binnen de vijf dagen te antwoorden. Die termijn is indicatief en houdt geen resultaatsverbintenis in.
      </>
    ),
  },
  {
    question: 'Mag ik meerdere huidproblemen in één gesprek bespreken?',
    answer: (
      <>
        Nee. Eén teleconsultatie gaat over één huidprobleem. Heeft u een tweede, apart huidprobleem, start dan een nieuwe conversatie zodat de dermatoloog elke klacht gericht kan beoordelen.
      </>
    ),
  },
  {
    question: 'Kan ik dit ook gebruiken voor mijn kind of iemand voor wie ik zorg?',
    answer: (
      <>
        Ja. Bij de start van een gesprek kiest u voor wie de consultatie is: uzelf, uw kind of iemand anders voor wie u zorgt.
      </>
    ),
  },
  {
    question: 'Wat als de arts geen advies kan geven op basis van mijn foto’s?',
    answer: (
      <>
        Is de aangeleverde informatie ontoereikend of is een onderzoek ter plaatse aangewezen, dan kan de arts een advies weigeren en u doorverwijzen, bijvoorbeeld naar uw huisarts of een raadpleging ter plaatse. In dat geval wordt het bedrag niet aangerekend of teruggestort.
      </>
    ),
  },
  {
    question: 'Wanneer moet ik toch een fysieke afspraak maken?',
    answer: (
      <>
        Bij acute of complexe problemen hoort u gewoon een afspraak te maken. Maar voor een opvolgingsvraag of een snelle beoordeling is een teleconsultatie een praktische tussenstap.
      </>
    ),
  },
  {
    question: 'Wanneer en hoe betaal ik?',
    answer: (
      <>
        U betaalt pas nadat de dermatoloog een advies heeft gegeven, via onze betaaldienstverlener Mollie. Het van toepassing zijnde tarief wordt duidelijk weergegeven voordat u uw aanvraag bevestigt.
      </>
    ),
  },
  {
    question: 'Waarom heeft Dermalink mijn rijksregisternummer nodig?',
    answer: (
      <>
        Uw rijksregisternummer laat de dermatoloog toe om uw identiteit te verifiëren, uw medisch dossier bij de praktijk sneller en correct terug te vinden, en het gebruiken bij een voorschrift op uw identiteitskaart. Het wordt met de grootste zorg behandeld: enkel gebruikt voor deze doeleinden en nooit voor andere doeleinden gedeeld.
      </>
    ),
  },
  {
    question: 'Houdt Dermalink een medisch dossier van mij bij?',
    answer: (
      <>
        Nee. Dermalink is geen medisch platform en houdt geen medisch dossier bij. Het is een communicatiekanaal tussen u en uw dermatoloog; de arts blijft eigenaar van het gesprek en de opvolging, net zoals bij een fysieke afspraak. Uw dermatoloog kan de gegevens uit het gesprek daarom overnemen in het patiëntendossier van de praktijk voor de verdere opvolging van uw zorg. Op dat moment gebeurt de verwerking niet langer op Dermalink, maar in het dossiersysteem van de praktijk zelf, waar ze net als bij een fysieke afspraak toegankelijk kan zijn voor een collega binnen diezelfde praktijk die bij uw opvolging betrokken is. Meer details vindt u in het Privacybeleid.
      </>
    ),
  },
];

function BenefitItem({ title, desc }: { title: string; desc: string }) {
  return (
    <li className={styles.benefitItem}>
      <span className={styles.checkCircle}>✓</span>
      <div>
        <p className={styles.benefitTitle}>{title}</p>
        <p className={styles.benefitDesc}>{desc}</p>
      </div>
    </li>
  );
}

export function PlatformInfo() {
  const location = useLocation();

  useEffect(() => {
    const id = location.hash.replace('#', '');
    if (!id) return;
    const el = document.getElementById(id);
    if (el instanceof HTMLDetailsElement) {
      el.open = true;
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [location.hash]);

  return (
    <PageLayout plain>
      <article className={styles.article}>
        <NavLink to="/" className={styles.back}>← Terug</NavLink>

        <h1 className={styles.h1}>
          <img src={logoIcon} alt="" aria-hidden="true" className={styles.logoIcon} />
          Over Dermalink
        </h1>

        <section className={styles.section}>
         <p className={styles.sectionLabel}>VOOR DE PATIËNT</p>
          <ul className={styles.benefitList}>
            <BenefitItem
              title="U betaalt pas na uw antwoord"
              desc="Geen abonnement, geen vooruitbetaling. U betaalt enkel wanneer u een antwoord van uw dermatoloog heeft ontvangen."
            />
            <BenefitItem
              title="Uw eigen dermatoloog"
              desc="Als uw dermatoloog is aangesloten bij Dermalink, gaat uw vraag rechtstreeks naar de arts die u al kent, niet naar de eerste beschikbare dermatoloog. Nog niet aangesloten? Vraag het gerust aan uw arts."
            />
            <BenefitItem
              title="U opent zelf een consultatie"
              desc="Geen afspraak, geen wachtkamer, geen receptioniste. U start een consultatie wanneer u dat nodig heeft, uw dermatoloog antwoordt zodra die tijd heeft."
            />
          </ul>

          <p className={styles.sectionLabel}>VOOR DE DERMATOLOOG</p>
          <ul className={styles.benefitList}>
            <BenefitItem
              title="Ontstaan uit de praktijk"
              desc="Dermalink is opgericht door De Huidspecialisten, na jaren waarin dermatologen elke avond onbetaalde en onveilige uren staken in losse e-mailwisselingen met patiënten. Vanuit die ervaring is Dermalink opgezet als eerlijk alternatief."
            />
            <BenefitItem
              title="Geen commerciële tussenlagen"
              desc="Geen partnerships rond producten, geen marketing, geen verkoopdoelstellingen. Geen compromissen met uw beroepsethiek: enkel een brug tussen u en uw patiënt."
            />
            <BenefitItem
              title="Geen maandelijkse licentiekosten"
              desc="Geen jaarlijkse of maandelijkse softwarelicentie. Van elk consult gaat een deel naar het platform, de rest gaat naar u. Bevalt het u niet? Dan kostte het u niets."
            />
          </ul>

          <div className={styles.pilotBox}>
            <p className={styles.pilotBadge}>PILOOTPROJECT</p>
            <p className={styles.pilotText}>
              We draaien momenteel een pilootproject met een beperkt aantal dermatologen. Bent u geïnteresseerd om aan te sluiten? Stuur gerust een mailtje naar <a href="mailto:info@dermalink.be">info@dermalink.be</a>. We nemen graag contact op.
            </p>
          </div>

          <p className={styles.sectionLabel}>ONZE MISSIE</p>
          <p className={styles.p}>
            Dermalink is in de eerste plaats een verlengstuk van de praktijk, geen commercieel product: het rechtstreekse kanaal tussen patiënt en de dermatoloog die hen al kent, zonder tussenlagen of verkoopdoelen.
          </p>
          <p className={styles.p}>
            Vandaag verloopt digitale communicatie met de arts nog te vaak via losse e-mails, met foto’s en informatie verspreid over verschillende berichten. Dermalink brengt alles samen op één veilige en overzichtelijke plek.
          </p>
        </section>

        <section className={styles.section}>
          <h2 className={styles.h2}>Veelgestelde vragen</h2>
          <div className={styles.faqList}>
            {FAQ_ITEMS.map((item) => (
              <details key={item.question} id={item.id} className={styles.faqItem}>
                <summary className={styles.faqQuestion}>
                  <span>{item.question}</span>
                  <ChevronDownIcon size={14} className={styles.faqChevron} />
                </summary>
                <div className={styles.faqAnswer}>{item.answer}</div>
              </details>
            ))}
          </div>
        </section>

        <p className={styles.trustMeta}>Veilig · Persoonlijk · Transparant</p>
      </article>
    </PageLayout>
  );
}
